import { useState, useRef, useCallback, useEffect } from 'react';
import { SideSheet } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { FileHierarchy } from '../../types';
import { uploadCut, uploadStem, uploadAudioFile, uploadVideoFile } from '../../api/files';
import { createVibe } from '../../api/vibes';
import { createCut } from '../../api/cuts';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  hierarchy: FileHierarchy[];
  defaultVibeId?: string;
  defaultCutId?: string;
  lockHierarchy?: boolean;
}

type FileType = 'CUT' | 'STEM' | 'VIDEO' | null;

export function UploadModal({
  isOpen,
  onClose,
  onSuccess,
  hierarchy,
  defaultVibeId,
  defaultCutId,
  lockHierarchy,
}: UploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<FileType>(null);
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState<string>('');
  const [vibeId, setVibeId] = useState<string>(defaultVibeId || '');
  const [cutId, setCutId] = useState<string>(defaultCutId || '');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [filesOnly, setFilesOnly] = useState(false);
  const [creatingVibe, setCreatingVibe] = useState(false);
  const [newVibeName, setNewVibeName] = useState('');
  const [creatingCut, setCreatingCut] = useState(false);
  const [newCutName, setNewCutName] = useState('');
  const [isCreatingVibe, setIsCreatingVibe] = useState(false);
  const [isCreatingCut, setIsCreatingCut] = useState(false);

  // Derive project from defaultVibeId when modal opens
  useEffect(() => {
    if (isOpen && defaultVibeId && hierarchy.length > 0) {
      const project = hierarchy.find(p => p.vibes?.some(v => v.id === defaultVibeId));
      if (project) {
        setProjectId(project.id);
      }
      setVibeId(defaultVibeId);
      setCutId(defaultCutId || '');
    }
  }, [isOpen, defaultVibeId, defaultCutId, hierarchy]);

  // Get vibes for selected project
  const selectedProject = hierarchy.find(p => p.id === projectId);
  const vibes = selectedProject?.vibes || [];
  
  // Get cuts for selected vibe
  const selectedVibe = vibes.find(v => v.id === vibeId);
  const cuts = selectedVibe?.cuts || [];
  const selectedCut = cuts.find(c => c.id === cutId);

  const detectFileType = (fileName: string): FileType => {
    const ext = fileName.toLowerCase().split('.').pop();
    if (ext === 'zip') return 'STEM';
    const audioExtensions = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'];
    if (audioExtensions.includes(ext || '')) return 'CUT';
    const videoExtensions = ['mp4', 'webm', 'mov', 'm4v', 'qt', 'mkv'];
    if (videoExtensions.includes(ext || '')) return 'VIDEO';
    return null;
  };

  const handleFileSelect = (selectedFile: File) => {
    const detectedType = detectFileType(selectedFile.name);
    if (!detectedType) {
      setError('Invalid file type. Please select an audio file (MP3, WAV, etc.), video file (MP4, WebM, MKV, etc.), or a ZIP file for stems.');
      return;
    }
    setFile(selectedFile);
    setFileType(detectedType);
    setError(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  }, []);

  const handleSubmit = async () => {
    if (!file || !cutId) {
      setError('Please select a file and a cut.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const options = {
        name: name.trim() || undefined,
        onProgress: setUploadProgress,
      };

      if (fileType === 'VIDEO') {
        await uploadVideoFile(cutId, file, options);
      } else if (fileType === 'CUT') {
        if (filesOnly) {
          await uploadAudioFile(cutId, file, options);
        } else {
          await uploadCut(cutId, file, options);
        }
      } else {
        await uploadStem(cutId, file, options);
      }

      setUploadProgress(100);

      // Reset form
      setFile(null);
      setFileType(null);
      setName('');
      if (!defaultVibeId) setVibeId('');
      if (!defaultCutId) setCutId('');
      
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setFile(null);
      setFileType(null);
      setName('');
      setError(null);
      setUploadProgress(0);
      setFilesOnly(false);
      setCreatingVibe(false);
      setCreatingCut(false);
      setNewVibeName('');
      setNewCutName('');
      onClose();
    }
  };

  const handleCreateVibe = async () => {
    if (!projectId || !newVibeName.trim()) return;
    setIsCreatingVibe(true);
    try {
      const vibe = await createVibe(projectId, { name: newVibeName.trim() });
      setVibeId(vibe.id);
      setCutId('');
      setNewVibeName('');
      setCreatingVibe(false);
      onSuccess?.();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create vibe');
    } finally {
      setIsCreatingVibe(false);
    }
  };

  const handleCreateCut = async () => {
    if (!vibeId || !newCutName.trim()) return;
    setIsCreatingCut(true);
    try {
      const cut = await createCut(vibeId, { name: newCutName.trim() });
      setCutId(cut.id);
      setNewCutName('');
      setCreatingCut(false);
      onSuccess?.();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create cut');
    } finally {
      setIsCreatingCut(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <SideSheet 
      isOpen={isOpen} 
      onClose={handleClose} 
      title="Upload File" 
      description="Upload audio files or stem packages to your project"
      size="lg"
      footer={
        <Button
          onClick={handleSubmit}
          isLoading={isUploading}
          disabled={!file || !cutId}
        >
          Upload {fileType === 'STEM' ? 'Stem' : 'Cut'}
        </Button>
      }
    >
      <div className="space-y-4">
        {/* Error Message */}
        {error && (
          <div className="p-3 bg-error/10 border border-error/30 rounded-lg text-error text-sm">
            {error}
          </div>
        )}

        {/* Drop Zone */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragOver
              ? 'border-primary bg-primary/10'
              : file
              ? 'border-primary/50 bg-primary/5'
              : 'border-border hover:border-muted'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,video/*,.zip,.mkv"
            onChange={handleFileChange}
            className="hidden"
          />
          
          {file ? (
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2">
                {fileType === 'CUT' ? (
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                  </div>
                )}
                <div className="text-left">
                  <p className="font-medium text-text">{file.name}</p>
                  <p className="text-sm text-muted">
                    {formatFileSize(file.size)} • {fileType === 'CUT' ? 'Audio File' : 'Stem Package'}
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted">Click or drop to replace</p>
            </div>
          ) : (
            <>
              <svg className="w-12 h-12 text-muted mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-text font-medium">Drop a file here or click to browse</p>
              <p className="text-sm text-muted mt-1">
                Audio files (MP3, WAV, etc.) for cuts • ZIP files for stems
              </p>
            </>
          )}
        </div>

        {/* Upload Progress Bar */}
        {isUploading && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted">Uploading...</span>
              <span className="text-text font-medium">{uploadProgress}%</span>
            </div>
            <div className="w-full h-2 bg-surface-light rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300 ease-out"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Name Input */}
        <Input
          label="Name (optional, recommended)"
          placeholder="e.g., Final Mix, Guitar Stems..."
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        {/* Files-only toggle for audio uploads — always visible, disabled until audio selected */}
        <div
          role="checkbox"
          aria-checked={filesOnly && fileType === 'CUT'}
          tabIndex={0}
          onClick={() => {
            if (fileType === 'CUT') setFilesOnly(!filesOnly);
          }}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && fileType === 'CUT') {
              e.preventDefault();
              setFilesOnly(!filesOnly);
            }
          }}
          className={`flex items-start gap-3 p-4 sm:p-3 rounded-lg transition-colors select-none ${
            fileType === 'CUT' ? 'cursor-pointer bg-surface-light border border-border' : 'bg-surface/50 border border-border/50'
          }`}
        >
          <input
            id="files-only-toggle"
            type="checkbox"
            checked={filesOnly}
            onChange={() => {}} // handled by parent div
            disabled={fileType !== 'CUT'}
            className="mt-0.5 w-5 h-5 sm:w-4 sm:h-4 rounded border-border accent-primary focus:ring-primary disabled:opacity-40 pointer-events-none"
          />
          <div className="flex-1 min-w-0">
            <span className={`text-sm font-medium block ${
              fileType === 'CUT' ? 'text-text' : 'text-muted'
            }`}>
              Store in Files only
            </span>
            <p className="text-xs text-muted mt-1 sm:mt-0.5 leading-relaxed">
              File will appear in the Files tab and global file explorer, but not in the Audio section for playback or comments.
            </p>
          </div>
        </div>

        {/* Hierarchy Selectors */}
        {lockHierarchy ? (
          <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-sm text-muted">
              Uploading to <span className="font-medium text-text">{selectedCut?.name}</span> in{" "}
              <span className="font-medium text-text">{selectedVibe?.name}</span>
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {/* Project Selector */}
            <div>
              <label className="block text-sm font-medium text-muted mb-1.5">
                Project
              </label>
              <select
                value={projectId}
                onChange={(e) => {
                  setProjectId(e.target.value);
                  setVibeId('');
                  setCutId('');
                }}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select a project...</option>
                {hierarchy.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Vibe Selector */}
            <div>
              <label className="block text-sm font-medium text-muted mb-1.5">
                Vibe <span className="text-error">*</span>
              </label>
              {creatingVibe ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newVibeName}
                      onChange={(e) => setNewVibeName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreateVibe();
                        if (e.key === 'Escape') { setCreatingVibe(false); setNewVibeName(''); }
                      }}
                      placeholder="Vibe name..."
                      className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <Button
                      size="sm"
                      onClick={handleCreateVibe}
                      isLoading={isCreatingVibe}
                      disabled={!newVibeName.trim()}
                    >
                      Create
                    </Button>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => { setCreatingVibe(false); setNewVibeName(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <select
                    value={vibeId}
                    onChange={(e) => {
                      setVibeId(e.target.value);
                      setCutId('');
                    }}
                    disabled={!projectId && !defaultVibeId}
                    className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select a vibe...</option>
                    {vibes.map((vibe) => (
                      <option key={vibe.id} value={vibe.id}>
                        {vibe.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setCreatingVibe(true)}
                    disabled={!projectId && !defaultVibeId}
                    title="Create new vibe"
                  >
                    + Vibe
                  </Button>
                </div>
              )}
            </div>

            {/* Cut Selector */}
            <div>
              <label className="block text-sm font-medium text-muted mb-1.5">
                Cut <span className="text-error">*</span>
              </label>
              {creatingCut ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newCutName}
                      onChange={(e) => setNewCutName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreateCut();
                        if (e.key === 'Escape') { setCreatingCut(false); setNewCutName(''); }
                      }}
                      placeholder="Cut name..."
                      className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <Button
                      size="sm"
                      onClick={handleCreateCut}
                      isLoading={isCreatingCut}
                      disabled={!newCutName.trim()}
                    >
                      Create
                    </Button>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => { setCreatingCut(false); setNewCutName(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <select
                    value={cutId}
                    onChange={(e) => setCutId(e.target.value)}
                    disabled={!vibeId}
                    className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select a cut...</option>
                    {cuts.map((cut) => (
                      <option key={cut.id} value={cut.id}>
                        {cut.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setCreatingCut(true)}
                    disabled={!vibeId}
                    title="Create new cut"
                  >
                    + Cut
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </SideSheet>
  );
}
